import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getDepartmentsRequest } from '../services/surveys';

export interface DepartmentsEntity {
  id: string;
  memberCount: {
    groups: string;
    participants: string;
    director?: string;
    employee?: string;
    manager?: string;
  };
  name: string;
  type: string;
}

interface UseFulfillDepartmentsParams {
  closeModal?: () => void;
  active?: boolean;
}

export const useFulfillDepartments = ({ active = false }: UseFulfillDepartmentsParams = {}) => {
  const params = useParams();
  const surveyId = params.surveyId || '1';
  const [departments, setDepartments] = useState<DepartmentsEntity[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const perPage = 10;
  const isLoadingRef = useRef(false);

  const loadDepartments = useCallback(
    async (pageNo: number, append: boolean = false) => {
      if (isLoadingRef.current) return;

      try {
        isLoadingRef.current = true;
        if (append) {
          setIsLoadingMore(true);
        } else {
          setLoading(true);
        }

        const response = await getDepartmentsRequest(surveyId, { pageNo, perPage });

        if (append) {
          setDepartments(prev => [...prev, ...response.items]);
        } else {
          setDepartments(response.items);
        }

        const totalPages = response.pagingInfo.totalPageCount || 0;
        setHasMore(pageNo < totalPages);
        setCurrentPage(pageNo);
        setError('');
      } catch (err) {
        setError('Failed to fetch departments');
        if (!append) {
          setDepartments([]);
        }
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        isLoadingRef.current = false;
      }
    },
    [surveyId, perPage]
  );

  const loadMore = useCallback(() => {
    if (!isLoadingRef.current && hasMore && active && !isLoadingMore) {
      loadDepartments(currentPage + 1, true);
    }
  }, [currentPage, hasMore, isLoadingMore, active, loadDepartments]);

  useEffect(() => {
    if (!active) {
      setDepartments([]);
      setCurrentPage(1);
      setHasMore(true);
      isLoadingRef.current = false;
      return;
    }

    loadDepartments(1, false);
  }, [surveyId, active, loadDepartments]);

  return { departments, isLoading, isLoadingMore, error, loadMore, hasMore };
};
